/**
 * Utility functions for converting Markdown to clean HTML
 */

/**
 * Convert Markdown to clean HTML
 * Removes all Markdown syntax and converts it to proper HTML elements
 * 
 * @param {string} markdownContent - The markdown content to convert
 * @returns {string} - Clean HTML content
 */
export function markdownToHTML(markdownContent) {
  if (!markdownContent) return '';

  let html = markdownContent;

  // Convert headers (H1-H6)
  html = html?.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  html = html?.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  html = html?.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  html = html?.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html?.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  html = html?.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // Convert bold text (**text** or __text__)
  html = html?.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html?.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Convert italic text (*text* or _text_)
  html = html?.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html?.replace(/_(.+?)_/g, '<em>$1</em>');

  // Convert bold+italic (***text*** or ___text___)
  html = html?.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html?.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');

  // Convert strikethrough (~~text~~)
  html = html?.replace(/~~(.+?)~~/g, '<del>$1</del>');

  // Convert inline code (`code`)
  html = html?.replace(/`(.+?)`/g, '<code>$1</code>');

  // Convert links [text](url)
  html = html?.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Convert images ![alt](url)
  html = html?.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

  // Convert unordered lists (* or - or +)
  const ulRegex = /^[\*\-\+]\s+(.+)$/gm;
  if (ulRegex?.test(html)) {
    html = html?.replace(ulRegex, '<li>$1</li>');
    html = html?.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
  }

  // Convert ordered lists (1. 2. 3.)
  const olRegex = /^\d+\.\s+(.+)$/gm;
  if (olRegex?.test(html)) {
    html = html?.replace(olRegex, '<li>$1</li>');
    html = html?.replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>');
  }

  // Convert code blocks (```language ... ```)
  html = html?.replace(/```(\w+)?\n([\s\S]+?)```/g, (match, lang, code) => {
    return `<pre><code class="language-${lang || 'plaintext'}">${code?.trim()}</code></pre>`;
  });

  // Convert blockquotes (> text)
  html = html?.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');

  // Convert horizontal rules (---, ___, ***)
  html = html?.replace(/^(---|\*\*\*|___)$/gm, '<hr />');

  // Convert line breaks (double newline to <p>)
  html = html?.split('\n\n')?.map(para => {
    if (!para?.trim()) return '';
    // Don't wrap if already has HTML tags
    if (/<(h[1-6]|ul|ol|pre|blockquote|hr)/?.test(para)) {
      return para;
    }
    return `<p>${para?.trim()}</p>`;
  })?.join('\n');

  // Convert single line breaks to <br>
  html = html?.replace(/\n(?!<)/g, '<br>\n');

  return html?.trim();
}

/**
 * Remove all Markdown syntax without converting to HTML
 * Useful for generating plain text from Markdown
 * 
 * @param {string} markdownContent - The markdown content to clean
 * @returns {string} - Plain text content
 */
export function stripMarkdown(markdownContent) {
  if (!markdownContent) return '';

  let text = markdownContent;

  // Remove headers
  text = text?.replace(/^#{1,6}\s+/gm, '');

  // Remove bold/italic
  text = text?.replace(/(\*\*|__)(.*?)\1/g, '$2');
  text = text?.replace(/(\*|_)(.*?)\1/g, '$2');

  // Remove links [text](url) -> text
  text = text?.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Remove images ![alt](url)
  text = text?.replace(/!\[[^\]]*\]\([^)]+\)/g, '');

  // Remove inline code
  text = text?.replace(/`([^`]+)`/g, '$1');

  // Remove code blocks
  text = text?.replace(/```[\s\S]*?```/g, '');

  // Remove blockquotes
  text = text?.replace(/^>\s+/gm, '');

  // Remove list markers
  text = text?.replace(/^[\*\-\+]\s+/gm, '');
  text = text?.replace(/^\d+\.\s+/gm, '');

  // Remove horizontal rules
  text = text?.replace(/^(---|\*\*\*|___)$/gm, '');

  return text?.trim();
}

/**
 * Sanitize HTML to prevent XSS attacks
 * Basic sanitization - for production use a library like DOMPurify
 * 
 * @param {string} html - HTML content to sanitize
 * @returns {string} - Sanitized HTML
 */
export function sanitizeHTML(html) {
  if (!html) return '';

  // Remove script tags
  let clean = html?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove event handlers
  clean = clean?.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');

  // Remove javascript: URLs
  clean = clean?.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '');

  return clean;
}

export const generateRecipeArticle = (
  recipeData,
  enableTastyRecipe,
  heroImageUrl,
  inlineImageUrl,
  faviconUrl
) => {
  const {
    recipeTitle,
    description,
    whyYoullLove = [],
    ingredients = [],
    instructions = [],
    prepTime,
    cookTime,
    servings,
    cuisine,
    difficulty,
    nutritionFacts = {},
    tags = [],
    category
  } = recipeData;

  const totalTime = parseInt(prepTime || 0) + parseInt(cookTime || 0);

  let content = '';

  // Hero image
  if (heroImageUrl) {
    content += `<div class="hero-image" style="background-image: url('${heroImageUrl}')"></div>\n\n`;
  }

  // Description
  if (description) {
    content += `<p class="recipe-description">${description}</p>\n\n`;
  }

  // Why You'll Love This Recipe - Wrapped in styled box
  if (whyYoullLove && whyYoullLove?.length > 0) {
    content += `<div class="love-box">\n`;
    content += `<h2>Why You'll Love This Recipe</h2>\n`;
    content += `<ul>\n`;
    whyYoullLove?.forEach(item => {
      const cleanItem = item?.replace(/^[-•]\s*/, '')?.trim();
      if (cleanItem) {
        content += `<li>${cleanItem}</li>\n`;
      }
    });
    content += `</ul>\n`;
    content += `</div>\n\n`;
  }

  // Ingredients
  if (ingredients && ingredients?.length > 0) {
    content += `<h2>Ingredients</h2>\n`;
    content += `<ul>\n`;
    ingredients?.forEach((item, index) => {
      content += `<li>${item}</li>\n`;
    });
    content += `</ul>\n\n`;
  }

  // Instructions
  if (instructions && instructions?.length > 0) {
    content += `<h2>Instructions</h2>\n`;
    content += `<ol>\n`;
    instructions?.forEach((step, index) => {
      content += `<li>${step}</li>\n`;
    });
    content += `</ol>\n\n`;
  }

  // Prep and Cook Time
  if (prepTime || cookTime) {
    content += `<div class="time-info">\n`;
    content += `<p>Prep Time: ${prepTime} min | Cook Time: ${cookTime} min | Total Time: ${totalTime} min</p>\n`;
    content += `</div>\n\n`;
  }

  // Servings
  if (servings) {
    content += `<p>Servings: ${servings}</p>\n\n`;
  }

  // Cuisine and Difficulty
  content += `<div class="recipe-meta">\n`;
  content += `<p><strong>Cuisine:</strong> ${cuisine}</p>\n`;
  content += `<p><strong>Difficulty:</strong> ${difficulty}</p>\n`;
  content += `</div>\n\n`;

  // Nutrition Facts
  if (nutritionFacts) {
    content += `<div class="nutrition-facts">\n`;
    content += `<h3>Nutrition Facts</h3>\n`;
    content += `<ul>\n`;
    Object.entries(nutritionFacts)?.forEach(([key, value]) => {
      if (value) {
        content += `<li><strong>${key}:</strong> ${value}</li>\n`;
      }
    });
    content += `</ul>\n`;
    content += `</div>\n\n`;
  }

  // Tags
  if (tags && tags?.length > 0) {
    content += `<div class="tags">\n`;
    content += `<p><strong>Tags:</strong> ${tags?.join(', ')}</p>\n`;
    content += `</div>\n\n`;
  }

  // Category
  if (category) {
    content += `<div class="category">\n`;
    content += `<p><strong>Category:</strong> ${category}</p>\n`;
    content += `</div>\n\n`;
  }

  return content;
};

export default {
  markdownToHTML,
  stripMarkdown,
  sanitizeHTML,
  generateRecipeArticle,
};